import React, { useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Heading5, Label, Body } from '~/components/atom/Typography';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import GridContainer from '~/components/atom/GridContainer';
import SelectBlock from '~/components/molecule/SelectBlock';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import Input from '~/components/molecule/Input';
import Icon from '~/components/atom/Icon';
import Divider from '~/components/atom/Divider';
import Status from '~/components/atom/Status';
import {
  Lfa_Conversion_Location_Type,
  type LfaServicesV2FieldsFragment,
  type GetLfaConversionDataQuery,
  type LfaServiceCalendarRefFieldsFragment,
} from '~/graphql/types';
import type {
  ServicesV2Action,
  ResolvedServiceV2,
} from '../../reducers/servicesV2Reducer';
import resolveServicesForOffice from '~/components/page/Settings/LFASettings/servicesV2/resolveServicesForOffice';
import {
  locationsEqual,
  calendarRefsEqual,
} from '~/components/page/Settings/LFASettings/servicesV2/comparisons';
import calendarAppliesToOffice from '~/components/page/Settings/LFASettings/servicesV2/calendarAppliesToOffice';
import formatOrdinal from '~/LFA/utils/formatOrdinal';
import LocationSettingsPopupV2 from './LocationSettingsPopupV2';
import CalendarSettingsPopupV2 from './CalendarSettingsPopupV2';
import hasValue from '~/util/hasValue';
import getDefaultCriteria from '~/components/page/Settings/LFASettings/servicesV2/defaultCriteria';

const text = {
  selectLocations: 'Locatieopties',
  locationError: 'Selecteer minimaal één locatie',
  criteria: 'Criteria voor agenten en AI',
  criterionPlaceholder: 'Voer een criterium in...',
  calendars: "Gekoppelde agenda's",
  noCalendars: "Geen beschikbare agenda's voor deze vestiging.",
  preferred: 'voorkeur',
  inheritedFromAccount: 'Account',
  removedFromAccount: 'Verwijderd t.o.v. account',
  removedFromAccountService: 'Verwijderd t.o.v. account',
  addedByOffice: 'Toegevoegd op vestiging',
  resetToAccount: 'Alles terugzetten naar account',
  accountLabel: 'Account',
  adjustedLabel: 'Afwijkend',
  accountDefault: 'Account:',
};

const locationLabelMap: Record<Lfa_Conversion_Location_Type, string> = {
  Office: 'Kantoor',
  OnLocation: 'Op locatie',
  Phone: 'Telefoon',
};

const AdjustedBadge = styled(JustificationContainer)(
  ({ theme }) => css`
    padding: 2px 8px;
    border: 1px dashed ${theme.color('secondary', 'base')};
    border-radius: ${theme.getTokens().border.radius.s};
    background-color: ${theme.color('secondary', 'translucent')};
    gap: ${theme.space('xxs')};
  `,
);

const ALL_LOCATION_TYPES: Array<Lfa_Conversion_Location_Type> = [
  Lfa_Conversion_Location_Type.Office,
  Lfa_Conversion_Location_Type.OnLocation,
  Lfa_Conversion_Location_Type.Phone,
];

type Props = {
  officeId: string;
  servicesV2: LfaServicesV2FieldsFragment;
  allServices: GetLfaConversionDataQuery['getLfaConversionData']['allServices'];
  dispatch: React.Dispatch<ServicesV2Action>;
};

type LocationType = ResolvedServiceV2['locations'][number];
type AccountCriterion = ResolvedServiceV2['criteria'][number];
type CalendarRef = ResolvedServiceV2['calendars'][number];

const OfficeLocationItem: React.FC<{
  locationType: Lfa_Conversion_Location_Type;
  location: LocationType | undefined;
  isLast: boolean;
  onToggle: () => void;
  onSave: (note: string, weight: number) => void;
}> = ({ locationType, location, isLast, onToggle, onSave }) => {
  const [showSettings, setShowSettings] = useState(false);
  const openerRef = useRef<HTMLDivElement>(null);
  const isEnabled = !!location;
  const note = location?.note?.trim();
  const isPreferred = isEnabled && location.weight > 0;

  return (
    <JustificationContainer width="100%" direction="column">
      <JustificationContainer
        width="100%"
        align="center"
        gap="s"
        justification="space-between"
      >
        <AnimatedCheckbox
          selectable
          label={
            <JustificationContainer align="center" gap="s">
              <Label size="base" margin={['xxxs', null, null, null]}>
                {locationLabelMap[locationType]}
                {hasValue(note) && `\u00a0(${note})`}
              </Label>
              {isPreferred && (
                <Status
                  label={`1ste ${text.preferred}`}
                  backgroundColor={{
                    group: 'success',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'success' }}
                />
              )}
            </JustificationContainer>
          }
          value={isEnabled}
          onChange={onToggle}
        />
        <Icon
          ref={openerRef}
          name="gear"
          onClick={() => isEnabled && setShowSettings(!showSettings)}
          style={{ visibility: isEnabled ? 'visible' : 'hidden' }}
          aria-hidden={!isEnabled}
        />
      </JustificationContainer>

      {showSettings && isEnabled && (
        <LocationSettingsPopupV2
          openerRef={openerRef}
          note={location.note ?? ''}
          weight={location.weight}
          onSave={onSave}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!isLast && <Divider margin={['xxs', null, null, null]} />}
    </JustificationContainer>
  );
};

const OfficeCriteriaList: React.FC<{
  service: ResolvedServiceV2;
  accountCriteria: Array<AccountCriterion>;
  onUpdate: (service: ResolvedServiceV2) => void;
}> = ({ service, accountCriteria, onUpdate }) => {
  const [newText, setNewText] = useState('');

  const accountCriteriaIds = useMemo(
    () => new Set(accountCriteria.map(c => c.id)),
    [accountCriteria],
  );

  const activeCriteriaIds = useMemo(
    () => new Set(service.criteria.map(c => c.id)),
    [service.criteria],
  );

  const removedAccountCriteria = useMemo(
    () => accountCriteria.filter(c => !activeCriteriaIds.has(c.id)),
    [accountCriteria, activeCriteriaIds],
  );

  const addCriterion = () => {
    if (!newText.trim()) return;
    onUpdate({
      ...service,
      criteria: [
        ...service.criteria,
        {
          __typename: 'LFA_Service_Criterion_V2' as const,
          id: uuid(),
          text: newText.trim(),
        },
      ],
    });
    setNewText('');
  };

  return (
    <JustificationContainer direction="column" gap="xs" width="100%">
      <Heading5 margin={[null]} fontWeight="regular">
        {text.criteria}
      </Heading5>

      {service.criteria.map(c => {
        const isFromAccount = accountCriteriaIds.has(c.id);
        return (
          <JustificationContainer
            key={c.id}
            align="center"
            gap="xs"
            width="100%"
          >
            <Body size="s" margin={[null]} style={{ flex: 1 }}>
              {c.text}
            </Body>
            {isFromAccount && (
              <Status
                label={text.accountLabel}
                backgroundColor={{
                  group: 'primary',
                  variant: 'translucent',
                }}
                textColor={{ group: 'primary' }}
              />
            )}
            <Icon
              name="trashcan"
              onClick={() =>
                onUpdate({
                  ...service,
                  criteria: service.criteria.filter(cr => cr.id !== c.id),
                })
              }
            />
          </JustificationContainer>
        );
      })}

      {removedAccountCriteria.map(c => (
        <JustificationContainer
          key={c.id}
          align="center"
          gap="xs"
          width="100%"
          style={{ opacity: 0.5 }}
        >
          <Body
            size="s"
            margin={[null]}
            color={{ group: 'tertiary' }}
            style={{ flex: 1, textDecoration: 'line-through' }}
          >
            {c.text}
          </Body>
          <Status
            label={text.accountLabel}
            backgroundColor={{
              group: 'primary',
              variant: 'translucent',
            }}
            textColor={{ group: 'primary' }}
          />
          <Icon
            name="rotate-ccw"
            onClick={() =>
              onUpdate({
                ...service,
                criteria: [...service.criteria, c],
              })
            }
          />
        </JustificationContainer>
      ))}

      <JustificationContainer align="center" gap="xs" width="100%">
        <Input
          type="text"
          value={newText}
          onChange={e => setNewText(e.target.value)}
          placeholder={text.criterionPlaceholder}
          width="100%"
          onKeyDown={e => {
            if (e.key === 'Enter') addCriterion();
          }}
        />
        <Button
          size="small"
          appearance="secondary"
          icon="plus"
          disabled={!newText.trim()}
          onClick={addCriterion}
        />
      </JustificationContainer>
    </JustificationContainer>
  );
};

const OfficeCalendarRow: React.FC<{
  calendar: { id: string; name: string };
  calendarRef: LfaServiceCalendarRefFieldsFragment | null;
  isInherited: boolean;
  isRemovedFromAccount: boolean;
  isLast: boolean;
  onToggle: () => void;
  onSave: (note: string, weight: number) => void;
  preferenceRank: number | null;
}> = ({
  calendar,
  calendarRef,
  isInherited,
  isRemovedFromAccount,
  isLast,
  onToggle,
  onSave,
  preferenceRank,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const openerRef = useRef<HTMLDivElement>(null);
  const isSelected = !!calendarRef;
  const note = calendarRef?.note?.trim();
  const isPreferred = isSelected && (calendarRef?.weight ?? 0) > 0;

  return (
    <JustificationContainer width="100%" direction="column">
      <JustificationContainer
        width="100%"
        align="center"
        gap="s"
        justification="space-between"
      >
        <AnimatedCheckbox
          selectable
          label={
            <JustificationContainer align="center" gap="s" wrap="wrap">
              <Label
                size="s"
                fontWeight="semiBold"
                margin={['xxxs', null, null, null]}
              >
                {calendar.name || calendar.id}
                {hasValue(note) && `\u00a0(${note})`}
              </Label>
              {isPreferred && preferenceRank != null && (
                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <Status
                    label={`${formatOrdinal(preferenceRank)} ${text.preferred} (${calendarRef.weight})`}
                    backgroundColor={{
                      group: 'success',
                      variant: 'translucent',
                    }}
                    textColor={{ group: 'success' }}
                  />
                </span>
              )}
              {isSelected && isInherited && (
                <Status
                  label={text.inheritedFromAccount}
                  backgroundColor={{
                    group: 'primary',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'primary' }}
                />
              )}
              {isRemovedFromAccount && (
                <Status
                  label={text.removedFromAccount}
                  backgroundColor={{
                    group: 'danger',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'danger' }}
                />
              )}
            </JustificationContainer>
          }
          value={isSelected}
          onChange={onToggle}
        />
        <Icon
          ref={openerRef}
          name="gear"
          onClick={() => isSelected && setShowSettings(!showSettings)}
          style={{ visibility: isSelected ? 'visible' : 'hidden' }}
          aria-hidden={!isSelected}
        />
      </JustificationContainer>

      {showSettings && calendarRef && (
        <CalendarSettingsPopupV2
          openerRef={openerRef}
          note={calendarRef.note ?? ''}
          weight={calendarRef.weight}
          onSave={onSave}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!isLast && <Divider margin={['xxs', null, null, null]} />}
    </JustificationContainer>
  );
};

const OfficeCalendarCheckboxes: React.FC<{
  service: ResolvedServiceV2;
  accountCalendars: Array<CalendarRef>;
  calendars: LfaServicesV2FieldsFragment['calendars'];
  officeId: string;
  onUpdate: (service: ResolvedServiceV2) => void;
}> = ({ service, accountCalendars, calendars, officeId, onUpdate }) => {
  const calendarsAdjusted = !calendarRefsEqual(
    service.calendars,
    accountCalendars,
  );

  const availableCalendars = useMemo(
    () => calendars.filter(c => calendarAppliesToOffice(c, officeId)),
    [calendars, officeId],
  );

  const preferenceRankMap = useMemo(() => {
    const ranked = service.calendars
      .filter(c => c.weight > 0)
      .sort((a, b) => b.weight - a.weight);
    return new Map(ranked.map((c, i) => [c.calendarId, i + 1]));
  }, [service.calendars]);

  const accountCalendarIdSet = useMemo(
    () => new Set(accountCalendars.map(c => c.calendarId)),
    [accountCalendars],
  );

  if (availableCalendars.length === 0) {
    return (
      <Body size="s" color={{ group: 'tertiary' }} margin={[null]}>
        {text.noCalendars}
      </Body>
    );
  }

  const refMap = new Map(service.calendars.map(c => [c.calendarId, c]));

  const toggle = (calendarId: string) => {
    const exists = refMap.has(calendarId);
    const next = exists
      ? service.calendars.filter(c => c.calendarId !== calendarId)
      : [
          ...service.calendars,
          {
            __typename: 'LFA_Service_Calendar_Ref' as const,
            calendarId,
            weight: 0,
            note: null,
          },
        ];
    onUpdate({ ...service, calendars: next });
  };

  const saveCalendarSettings = (
    calendarId: string,
    note: string,
    weight: number,
  ) => {
    onUpdate({
      ...service,
      calendars: service.calendars.map(c =>
        c.calendarId === calendarId ? { ...c, note: note || null, weight } : c,
      ),
    });
  };

  const resetCalendars = () =>
    onUpdate({ ...service, calendars: [...accountCalendars] });

  return (
    <JustificationContainer direction="column" gap="xs" width="100%">
      <JustificationContainer
        align="center"
        gap="s"
        justification="space-between"
        width="100%"
      >
        <JustificationContainer align="center" gap="s">
          <Heading5 margin={[null]} fontWeight="regular">
            {text.calendars}
          </Heading5>
          {calendarsAdjusted && (
            <AdjustedBadge align="center">
              <Icon name="edit" size="s" color={{ group: 'secondary' }} />
              <Body size="s" margin={[null]} color={{ group: 'secondary' }}>
                {text.adjustedLabel}
              </Body>
            </AdjustedBadge>
          )}
        </JustificationContainer>
        {calendarsAdjusted && (
          <Icon name="rotate-ccw" onClick={resetCalendars} />
        )}
      </JustificationContainer>

      {availableCalendars.map((cal, idx) => {
        const isRemovedFromAcct =
          accountCalendarIdSet.has(cal.id) && !refMap.has(cal.id);
        return (
          <OfficeCalendarRow
            key={cal.id}
            calendar={cal}
            calendarRef={refMap.get(cal.id) ?? null}
            isInherited={accountCalendarIdSet.has(cal.id)}
            isRemovedFromAccount={isRemovedFromAcct}
            isLast={idx === availableCalendars.length - 1}
            onToggle={() => toggle(cal.id)}
            onSave={(note, weight) =>
              saveCalendarSettings(cal.id, note, weight)
            }
            preferenceRank={preferenceRankMap.get(cal.id) ?? null}
          />
        );
      })}
    </JustificationContainer>
  );
};

const OfficeServicesView: React.FC<Props> = ({
  officeId,
  servicesV2,
  allServices,
  dispatch,
}) => {
  const resolved = useMemo(
    () =>
      resolveServicesForOffice({
        accountServices: servicesV2.accountServices,
        officeChangesets: servicesV2.officeChangesets,
        officeId,
      }),
    [officeId, servicesV2.accountServices, servicesV2.officeChangesets],
  );

  const allCalendars = servicesV2.calendars;

  const hasOfficeChanges = servicesV2.officeChangesets.some(
    e => e.officeId === officeId,
  );

  const dispatchResolved = (nextServices: Array<ResolvedServiceV2>) => {
    dispatch({
      type: 'V2_SET_OFFICE_RESOLVED_SERVICES',
      payload: { officeId, resolvedServices: nextServices },
    });
  };

  const toggleService = (serviceId: string) => {
    const exists = resolved.some(s => s.id === serviceId);
    if (exists) {
      dispatchResolved(resolved.filter(s => s.id !== serviceId));
    } else {
      const accountService = servicesV2.accountServices.find(
        s => s.id === serviceId,
      );
      const accountCriteria = accountService?.criteria ?? [];
      const existingTexts = new Set(accountCriteria.map(c => c.text));
      const defaults = getDefaultCriteria(serviceId).filter(
        c => !existingTexts.has(c.text),
      );
      const criteria = [...accountCriteria, ...defaults];
      const base = accountService
        ? {
            id: accountService.id,
            locations: [...accountService.locations],
            criteria,
            calendars: [...accountService.calendars],
          }
        : {
            id: serviceId,
            locations: [],
            criteria,
            calendars: [],
          };
      dispatchResolved([...resolved, base]);
    }
  };

  const updateService = (updated: ResolvedServiceV2) =>
    dispatchResolved(resolved.map(s => (s.id === updated.id ? updated : s)));

  const toggleLocation = (
    serviceId: string,
    locationType: Lfa_Conversion_Location_Type,
  ) => {
    dispatchResolved(
      resolved.map(s => {
        if (s.id !== serviceId) return s;
        const has = s.locations.some(l => l.type === locationType);
        if (has && s.locations.length <= 1) return s;
        return {
          ...s,
          locations: has
            ? s.locations.filter(l => l.type !== locationType)
            : [
                ...s.locations,
                {
                  __typename: 'LFA_Account_Service_Location_V2' as const,
                  type: locationType,
                  weight: 0,
                  note: null,
                },
              ],
        };
      }),
    );
  };

  const saveLocation = (
    serviceId: string,
    locationType: Lfa_Conversion_Location_Type,
    note: string,
    weight: number,
  ) => {
    dispatchResolved(
      resolved.map(s => {
        if (s.id !== serviceId) return s;
        return {
          ...s,
          locations: s.locations.map(l =>
            l.type === locationType ? { ...l, note, weight } : l,
          ),
        };
      }),
    );
  };

  const resetAllLocations = (serviceId: string) => {
    const accountService = servicesV2.accountServices.find(
      s => s.id === serviceId,
    );
    if (!accountService) return;
    dispatchResolved(
      resolved.map(s =>
        s.id === serviceId
          ? { ...s, locations: [...accountService.locations] }
          : s,
      ),
    );
  };

  const resetToAccount = () =>
    dispatchResolved(
      servicesV2.accountServices.map(s => ({
        id: s.id,
        locations: [...s.locations],
        criteria: [...s.criteria],
        calendars: [...s.calendars],
      })),
    );

  const serviceDiffersFromAccount = (
    serviceId: string,
    officeSvc: ResolvedServiceV2 | undefined,
  ): boolean => {
    const acctSvc = servicesV2.accountServices.find(s => s.id === serviceId);
    if (!acctSvc && !officeSvc) return false;
    if (!acctSvc || !officeSvc) return true;
    if (!locationsEqual(officeSvc.locations, acctSvc.locations)) return true;
    if (!calendarRefsEqual(officeSvc.calendars, acctSvc.calendars)) return true;
    const acctIds = new Set(acctSvc.criteria.map(c => c.id));
    const officeIds = new Set(officeSvc.criteria.map(c => c.id));
    if (acctIds.size !== officeIds.size) return true;
    for (const id of acctIds) {
      if (!officeIds.has(id)) return true;
    }
    return false;
  };

  return (
    <JustificationContainer direction="column" gap="m" width="100%">
      {hasOfficeChanges && (
        <Button
          size="small"
          appearance="secondary"
          label={text.resetToAccount}
          icon="reload"
          onClick={resetToAccount}
        />
      )}

      <GridContainer width="100%" gap="m" columns="repeat(2, 1fr)">
        {allServices.map(option => {
          const service = resolved.find(s => s.id === option.id);
          const isSelected = !!service;
          const accountService = servicesV2.accountServices.find(
            s => s.id === option.id,
          );
          const isRemovedByOffice = !!accountService && !isSelected;
          const isAddedByOffice = !accountService && isSelected;
          const hasDiff = serviceDiffersFromAccount(option.id, service);

          const locationsAdjusted =
            isSelected &&
            accountService &&
            !locationsEqual(service?.locations ?? [], accountService.locations);

          const titleNode = hasDiff ? (
            <JustificationContainer align="center" gap="s" wrap="wrap">
              <Label
                margin={['xxxs', null, null, 'xxxs']}
                color={{ group: 'secondary' }}
                size="base"
              >
                {option.name}
              </Label>
              {isRemovedByOffice && (
                <Status
                  label={text.removedFromAccountService}
                  backgroundColor={{
                    group: 'danger',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'danger' }}
                />
              )}
              {isAddedByOffice && (
                <Status
                  label={text.addedByOffice}
                  backgroundColor={{
                    group: 'success',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'success' }}
                />
              )}
              {!isRemovedByOffice && !isAddedByOffice && (
                <AdjustedBadge align="center">
                  <Icon name="edit" size="s" color={{ group: 'secondary' }} />
                  <Body size="s" margin={[null]} color={{ group: 'secondary' }}>
                    {text.adjustedLabel}
                  </Body>
                </AdjustedBadge>
              )}
            </JustificationContainer>
          ) : (
            option.name
          );

          return (
            <SelectBlock
              type="checkbox"
              key={option.id}
              checkboxTitle={titleNode}
              description={option.description}
              checked={isSelected}
              margin={[null]}
              checkboxOnly
              onClick={() => toggleService(option.id)}
            >
              {isSelected && service && (
                <JustificationContainer
                  width="100%"
                  padding={['s', 'xl', null, 'xl']}
                  direction="column"
                  gap="s"
                  onClick={e => e.stopPropagation()}
                  style={{ cursor: 'initial' }}
                >
                  <Divider margin={[null]} />

                  <JustificationContainer
                    align="center"
                    gap="s"
                    justification="space-between"
                    width="100%"
                  >
                    <JustificationContainer align="center" gap="s">
                      <Heading5 margin={[null]} fontWeight="regular" required>
                        {text.selectLocations}
                      </Heading5>
                      {locationsAdjusted && (
                        <AdjustedBadge align="center">
                          <Icon
                            name="edit"
                            size="s"
                            color={{ group: 'secondary' }}
                          />
                          <Body
                            size="s"
                            margin={[null]}
                            color={{ group: 'secondary' }}
                          >
                            {text.adjustedLabel}
                          </Body>
                        </AdjustedBadge>
                      )}
                    </JustificationContainer>
                    {locationsAdjusted && (
                      <Icon
                        name="rotate-ccw"
                        onClick={() => resetAllLocations(option.id)}
                      />
                    )}
                  </JustificationContainer>

                  {service.locations.length === 0 && (
                    <JustificationContainer
                      align="center"
                      gap="xs"
                      width="100%"
                    >
                      <Status
                        label={text.locationError}
                        backgroundColor={{
                          group: 'danger',
                          variant: 'translucent',
                        }}
                        textColor={{ group: 'danger' }}
                      />
                    </JustificationContainer>
                  )}

                  <JustificationContainer
                    direction="column"
                    gap="xs"
                    width="100%"
                  >
                    {ALL_LOCATION_TYPES.map((locationType, index) => (
                      <OfficeLocationItem
                        key={locationType}
                        locationType={locationType}
                        location={service.locations.find(
                          l => l.type === locationType,
                        )}
                        isLast={index === ALL_LOCATION_TYPES.length - 1}
                        onToggle={() => toggleLocation(option.id, locationType)}
                        onSave={(note, weight) =>
                          saveLocation(option.id, locationType, note, weight)
                        }
                      />
                    ))}
                  </JustificationContainer>

                  <Divider margin={['xxs', null]} />

                  <OfficeCriteriaList
                    service={service}
                    accountCriteria={accountService?.criteria ?? []}
                    onUpdate={updateService}
                  />

                  <Divider margin={['xxs', null]} />

                  <OfficeCalendarCheckboxes
                    service={service}
                    accountCalendars={accountService?.calendars ?? []}
                    calendars={allCalendars}
                    officeId={officeId}
                    onUpdate={updateService}
                  />
                </JustificationContainer>
              )}
            </SelectBlock>
          );
        })}
      </GridContainer>
    </JustificationContainer>
  );
};

export default OfficeServicesView;
